<script setup lang="ts">
import MList from "../../MList";
import MItem from "../../MItem";
import MItemLabel from "../../MItemLabel";
import MItemSection from "../../MItemSection";
import { ref, computed, watch, nextTick } from "vue";
import MMenu from "../../MMenu";
import MInput from "../MInput";
import MLayoutStack from "../../MLayoutStack";
import MModal from "../../MModal";
import MButton from "../../MButton";

const props = defineProps<{
  modelValue?: string;
  variableOptions: Array<{ label: string; value: string }>;
  label?: string;
  allowCustomVariables: boolean;
  required: boolean;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: string): void;
  (e: "customVariableCreated", value: { name: string; value: string; defaultText: string }): void;
}>();

// Wraps MInput — keydown events from the textarea bubble up here.
const wrapperEl = ref<HTMLElement | null>(null);

// Invisible fixed span teleported to <body> and repositioned to the {{ cursor location.
// Keeping it in body means position:fixed is always viewport-relative, which avoids the
// drag-modal transform context that would otherwise make MMenu coordinates wrong.
const caretMarkerEl = ref<HTMLElement | null>(null);

const showCustomVariableModal = ref<boolean>(false);
const customVariable_name = ref<string>("");
const customVariable_defaultText = ref<string>("");
const customVariables = ref<{ name: string; value: string; defaultText: string }[]>([]);

const showMenu = computed(() => getLastUnclosedDoubleBraceContent(props.modelValue) !== null);

const filteredActions = computed(() => {
  const unclosed = getLastUnclosedDoubleBraceContent(props.modelValue);
  if (!unclosed) return [];
  const openVariable = unclosed.substring(2);
  // Only search the text typed before the first space so `{{pat ` doesn't filter out everything
  const searchableText = openVariable.match(/^([a-zA-Z0-9_]*)/)[0];

  const q = searchableText.toLowerCase();
  const filteredList = props.variableOptions.filter((v) => {
    return v.value.toLowerCase().includes(q) || v.label.toLowerCase().includes(q);
  });
  if (props.allowCustomVariables) {
    filteredList.push({ label: "Add custom variable...", value: "custom" });
  }

  return filteredList;
});

// Move the caret marker to the viewport position of the last unclosed {{ in the textarea.
// Uses a mirror div so we don't need a canvas or Range API.
async function updateMarkerPosition() {
  await nextTick();
  const marker = caretMarkerEl.value;
  if (!marker) return;
  const ta = document.getElementById("bodyContent") as HTMLTextAreaElement | null;
  if (!ta) return;
  const text = props.modelValue ?? "";
  const unclosed = getLastUnclosedDoubleBraceContent(text);
  if (!unclosed) return;
  const openStart = text.length - unclosed.length;

  const cs = window.getComputedStyle(ta);
  const mirror = document.createElement("div");
  for (const p of [
    "fontFamily",
    "fontSize",
    "fontWeight",
    "fontStyle",
    "letterSpacing",
    "lineHeight",
    "paddingTop",
    "paddingLeft",
    "paddingRight",
    "paddingBottom",
    "borderTopWidth",
    "borderLeftWidth",
    "boxSizing",
  ] as const) {
    mirror.style[p] = cs[p];
  }
  mirror.style.position = "absolute";
  mirror.style.visibility = "hidden";
  mirror.style.top = "-9999px";
  mirror.style.left = "-9999px";
  mirror.style.width = ta.clientWidth + "px";
  mirror.style.whiteSpace = "pre-wrap";
  mirror.style.wordBreak = "break-word";
  mirror.style.overflow = "hidden";

  mirror.textContent = text.substring(0, openStart);
  const span = document.createElement("span");
  span.textContent = "​"; // zero-width space — gives the span measurable height
  mirror.appendChild(span);
  document.body.appendChild(mirror);

  const taRect = ta.getBoundingClientRect();
  const lineH = parseInt(cs.lineHeight, 10) || 20;
  const charTop = span.offsetTop - ta.scrollTop;
  const charLeft = span.offsetLeft;
  document.body.removeChild(mirror);

  // Position the marker spanning the full line containing {{ so that:
  // - spaceBelow is measured from the line bottom (rectInput.bottom)
  // - when flipping above, rectInput.top is the line top so the menu clears the text
  marker.style.top = `${taRect.top + charTop}px`;
  marker.style.left = `${taRect.left + charLeft}px`;
  marker.style.height = `${lineH}px`;
}

watch(showMenu, (v) => {
  if (v) updateMarkerPosition();
});
watch(
  () => props.modelValue,
  () => {
    if (showMenu.value) updateMarkerPosition();
  },
);

// --- keyboard navigation ---

function menuItems(): HTMLElement[] {
  // data-m-token-menu is on the <m-list> root element (attr falls through in Vue 3)
  return Array.from(document.querySelectorAll<HTMLElement>("[data-m-token-menu] .m-item"));
}

function handleWrapperKeydown(e: KeyboardEvent) {
  if (!showMenu.value) return;
  if (e.key === "ArrowDown") {
    e.preventDefault();
    menuItems()[0]?.focus();
  }
}

function handleMenuKeydown(e: KeyboardEvent) {
  const items = menuItems();
  const idx = items.indexOf(document.activeElement as HTMLElement);
  if (e.key === "ArrowDown") {
    e.preventDefault();
    (items[idx + 1] ?? items[0])?.focus();
  } else if (e.key === "ArrowUp") {
    e.preventDefault();
    idx <= 0 ? focusOnContent() : items[idx - 1]?.focus();
  } else if (e.key === "Enter") {
    e.preventDefault();
    (document.activeElement as HTMLElement | null)?.click();
  } else if (e.key === "Escape") {
    e.preventDefault();
    focusOnContent();
  }
}

// --- existing logic ---

function getLastUnclosedDoubleBraceContent(text: string): string | null {
  if (!text) return null;
  const openRe = /\{\{/g;
  const closeRe = /\}\}/g;
  let match: RegExpExecArray | null;
  let stack: number[] = [];

  // Track all opening positions
  while ((match = openRe.exec(text)) !== null) {
    stack.push(match.index);
  }

  // For each closing `}}`, pop an opening `{{`
  while ((match = closeRe.exec(text)) !== null) {
    let keyToRemove: number | null = null;
    if (stack.length > 0) {
      for (const stackKey in stack) {
        const openBracePos = stack[stackKey];
        if (match.index > openBracePos) {
          keyToRemove = Number(stackKey);
        }
        if (openBracePos > match.index) {
          break;
        }
      }
      if (keyToRemove != null) {
        stack[keyToRemove] = NaN;
      }
    }
  }

  stack = stack.filter((k) => !isNaN(k));

  // If anything remains in stack, it's an unmatched `{{`
  if (stack.length > 0) {
    const lastOpenIndex = stack[stack.length - 1];
    return text.slice(lastOpenIndex);
  }
  return null;
}

function selectVariable(variable) {
  if (variable.value === "custom") {
    if (props.allowCustomVariables) {
      showCustomVariableModal.value = true;
    }
    return;
  }
  insertVariableIntoBody(variable.value);
}

async function insertVariableIntoBody(variable: string) {
  const text = props.modelValue ?? "";
  const unclosed = getLastUnclosedDoubleBraceContent(text);
  if (!unclosed) return;
  const openStart = text.length - unclosed.length;
  // Drop the partial text typed after `{{` (e.g. `{{pat` → strip `pat`)
  const afterOpen = unclosed.slice(2).replace(/^[a-zA-Z0-9_]+[ \n]?/, "");
  const inserted = `{{${variable}}} `;
  const newValue = (text.substring(0, openStart) + inserted + afterOpen).trim();
  emit("update:modelValue", newValue);
  await focusOnContent(openStart + inserted.length);
}

async function focusOnContent(caretPos?: number) {
  await nextTick();
  const ta = document.getElementById("bodyContent") as HTMLTextAreaElement | null;
  ta?.focus();
  if (caretPos !== undefined) {
    ta?.setSelectionRange(caretPos, caretPos);
  }
}

function isCustomVariableValid() {
  return validateCustomVariable() !== null || "This custom variable name is already in use.";
}

function validateCustomVariable() {
  // remove all symbols, lowercase, replace spaces with underscores
  // e.g. "How are you?" -> "how_are_you"
  const varName = customVariable_name.value
    .toLowerCase()
    .trim()
    .replaceAll(" ", "_")
    .replace(/\W/g, "");

  const variableExists = customVariables.value.some((v) => v.value === varName);
  return variableExists ? null : varName;
}

function addCustomVariable() {
  const variable = validateCustomVariable();
  // do not add as the variable already exists
  if (variable === null) return;

  const newVariable = {
    name: customVariable_name.value,
    value: variable,
    defaultText: customVariable_defaultText.value,
  };

  customVariables.value.push(newVariable);
  insertVariableIntoBody(variable + "|nl2br");

  // reset and close modal
  customVariable_name.value = "";
  customVariable_defaultText.value = "";
  showCustomVariableModal.value = false;

  emit("customVariableCreated", newVariable);
}
</script>

<template>
  <!--
    Invisible anchor teleported to <body> so position:fixed is always viewport-relative.
    Repositioned to the {{ cursor location by updateMarkerPosition() and used as the
    MMenu target so the dropdown appears next to the trigger char, not the textarea bottom.
  -->
  <teleport to="body">
    <span
      v-if="showMenu"
      ref="caretMarkerEl"
      style="position: fixed; width: 0; height: 0; pointer-events: none; overflow: hidden"
      aria-hidden="true"
    />
  </teleport>
  <div ref="wrapperEl" @keydown="handleWrapperKeydown">
    <m-input
      id="bodyContent"
      :label="label || 'Body content'"
      type="textarea"
      :model-value="modelValue"
      :required="required"
      @update:model-value="(val) => emit('update:modelValue', val)"
    />
  </div>
  <div id="canvas-text-area">
    <m-menu
      v-if="showMenu"
      persistent
      render-target="body"
      :target="caretMarkerEl"
      :focus-container="wrapperEl"
      :model-value="true"
      :max-height="300"
    >
      <m-list
        v-if="filteredActions.length > 0"
        ref="list"
        class="scroll-area"
        style="min-width: 250px"
        dense
        tabindex="0"
        role="listbox"
        data-m-token-menu
        @keydown="handleMenuKeydown"
      >
        <m-item
          v-for="(variable, i) in filteredActions"
          :id="`${variable}-${i}`"
          :key="`${variable}-${i}`"
          ref="refActionMenuItems"
          clickable
          tabindex="-1"
          role="option"
          @click="() => selectVariable(variable)"
        >
          <m-item-section>
            <m-item-label>
              {{ variable.label }}
            </m-item-label>
          </m-item-section>
        </m-item>
      </m-list>
      <m-list v-else-if="props.allowCustomVariables">
        <m-item
          ref="refActionMenuItems"
          class="label empty-text"
          clickable
          focused
          @click="() => customVariable()"
        >
          <m-item-section>
            <m-item-label class="custom-variable-text">Add custom variable...</m-item-label>
          </m-item-section>
        </m-item>
      </m-list>
    </m-menu>
  </div>
  <m-modal
    v-if="props.allowCustomVariables"
    v-model="showCustomVariableModal"
    title="Add Custom Field"
    type="small"
  >
    <m-layout-stack>
      <m-input
        v-model="customVariable_name"
        label="Name"
        required
        :rules="[isCustomVariableValid]"
      />
      <m-input v-model="customVariable_defaultText" label="Default text" type="textarea" />
      <m-layout-stack horizontal end>
        <m-button
          label="Cancel"
          color="secondary"
          @click="
            () => {
              customVariable_name.value = null;
              customVariable_defaultText.value = null;
              showCustomVariableModal = false;
            }
          "
        />
        <m-button label="Add" :disable="!customVariable_name" @click="addCustomVariable" />
      </m-layout-stack>
    </m-layout-stack>
  </m-modal>
</template>
